import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { fullBlogPosts } from '../data/mock';
import { Card, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, ArrowLeft, Clock, Calendar, User, Tag, Share2, Linkedin, Twitter, Facebook } from 'lucide-react';
import { SEO } from '../components/SEO';

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  const categories = ['All', 'Trade Guide', 'Market Insights', 'Industry Trends', 'Compliance'];
  
  const filteredPosts = selectedCategory === 'All' 
    ? fullBlogPosts 
    : fullBlogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen">
      <SEO
        title="Blog"
        path="/blog"
        description="Expert articles on international trade, Australian exports, market insights, and supply chain management from Naviton Global."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-navy-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-500/20 text-teal-400 text-sm font-medium mb-6">
                Naviton Global Blog
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Trade Insights &
                <span className="block text-teal-400">Industry Intelligence</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                In-depth articles on international trade, market analysis, compliance guides, 
                and industry trends to help your business succeed globally.
              </p>
            </div>
          </div>
        </section>

        {/* Category Filters */}
        <section className="py-8 bg-slate-50 border-b border-slate-200 sticky top-20 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === cat
                      ? 'bg-navy-900 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Post */}
        {selectedCategory === 'All' && (
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-8">Featured Article</h2>
              <Link to={`/blog/${fullBlogPosts[0].slug}`}>
                <Card className="group overflow-hidden border-slate-200 hover:border-teal-300 hover:shadow-xl transition-all duration-300">
                  <div className="md:grid md:grid-cols-2">
                    <div className="h-64 md:h-full md:min-h-[400px] overflow-hidden">
                      <img 
                        src={fullBlogPosts[0].image} 
                        alt={fullBlogPosts[0].title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-4">
                        <Badge variant="secondary" className="bg-teal-100 text-teal-700">
                          {fullBlogPosts[0].category}
                        </Badge>
                        <Badge variant="outline" className="border-amber-300 text-amber-700">
                          Featured
                        </Badge>
                      </div>
                      <CardTitle className="text-2xl md:text-3xl text-navy-900 mb-4 group-hover:text-teal-700 transition-colors">
                        {fullBlogPosts[0].title}
                      </CardTitle>
                      <CardDescription className="text-slate-600 mb-6 text-lg">
                        {fullBlogPosts[0].excerpt}
                      </CardDescription>
                      <div className="flex items-center gap-6 text-sm text-slate-500 mb-6">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {new Date(fullBlogPosts[0].date).toLocaleDateString('en-AU', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          {fullBlogPosts[0].readTime}
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          {fullBlogPosts[0].author}
                        </div>
                      </div>
                      <div className="inline-flex items-center text-teal-600 font-medium group-hover:text-teal-700">
                        Read Full Article
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            </div>
          </section>
        )}

        {/* Blog Posts Grid */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-8">
              {selectedCategory === 'All' ? 'All Articles' : selectedCategory}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`}>
                  <Card className="group h-full overflow-hidden border-slate-200 hover:border-teal-300 hover:shadow-xl transition-all duration-300 bg-white">
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge variant="secondary" className="bg-teal-100 text-teal-700 text-xs">
                          {post.category}
                        </Badge>
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {post.readTime}
                        </span>
                      </div>
                      <CardTitle className="text-lg text-navy-900 mb-2 group-hover:text-teal-700 transition-colors line-clamp-2">
                        {post.title}
                      </CardTitle>
                      <CardDescription className="text-slate-600 mb-4 text-sm line-clamp-2">
                        {post.excerpt}
                      </CardDescription>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">
                          {new Date(post.date).toLocaleDateString('en-AU', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                        <span className="text-teal-600 font-medium flex items-center gap-1 group-hover:text-teal-700">
                          Read
                          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-20 bg-navy-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Get Trade Insights Delivered
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              Subscribe to receive our latest articles, market updates, and trade intelligence directly to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 rounded-xl border-0 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button className="px-8 py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl transition-colors">
                Subscribe
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
